import React from "react";
import { Backdrop, Box, useMediaQuery } from "@mui/material";

export const GIF_OVERLAY_SRC = `${process.env.PUBLIC_URL}/permission-denied.gif`;
export const OVERLAY_DURATION_MS = 3000;
const GIF_OVERLAY_EVENT = "internal-admin:show-gif-overlay";
const isLoginPage = () => window.location.hash.startsWith("#/login");

/*
 * The auth provider lives outside React, so it asks for the takeover with a
 * window event rather than through component state.
 */
export const showGifOverlay = () => window.dispatchEvent(new Event(GIF_OVERLAY_EVENT));

/*
 * Whenever our API answers with a 403, or a 401 logs the admin out, the gif in
 * `public/permission-denied.gif` takes over the page for a few seconds once it
 * has loaded (clicking dismisses it early). It's mounted beside <Admin> rather
 * than in the layout so it survives the redirect to the login page, and sits
 * just under react-admin's snackbar so any error message stays readable on top
 * of it. If the gif is missing, or the admin prefers reduced motion, we skip
 * the takeover.
 */
export const GifOverlay = () => {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // `showCount` is bumped on every takeover so the img remounts and fires onLoad again, even when cached
  const [{ gifState, showCount }, setOverlay] = React.useState({ gifState: "idle", showCount: 0 });
  const setGifState = (state) => setOverlay((overlay) => ({ ...overlay, gifState: state }));

  React.useEffect(() => {
    if (prefersReducedMotion) return;
    // react-admin reports every failed request, so a page with several failing
    // queries triggers this several times; let the takeover already running finish.
    const show = () =>
      setOverlay((overlay) =>
        overlay.gifState === "loading" || overlay.gifState === "showing"
          ? overlay
          : { gifState: "loading", showCount: overlay.showCount + 1 }
      );
    window.addEventListener(GIF_OVERLAY_EVENT, show);
    return () => window.removeEventListener(GIF_OVERLAY_EVENT, show);
  }, [prefersReducedMotion]);

  React.useEffect(() => {
    if (gifState !== "showing") return;
    const timer = setTimeout(() => setGifState("done"), OVERLAY_DURATION_MS);
    return () => clearTimeout(timer);
  }, [gifState]);

  // This sits outside react-admin's router, so it follows the hash itself
  const [onLoginPage, setOnLoginPage] = React.useState(isLoginPage());
  React.useEffect(() => {
    const update = () => setOnLoginPage(isLoginPage());
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);

  /*
   * After a 401 the takeover plays over the login page. There the admin's first
   * click should still reach "Sign in with Google", so clicks pass through the
   * gif and close it on the way. Elsewhere the gif swallows the click instead,
   * since the page underneath is hidden and it could land on any button.
   */
  React.useEffect(() => {
    if (!onLoginPage || gifState !== "showing") return;
    const dismiss = () => setGifState("done");
    window.addEventListener("pointerdown", dismiss, true);
    return () => window.removeEventListener("pointerdown", dismiss, true);
  }, [onLoginPage, gifState]);

  if (prefersReducedMotion || gifState === "idle" || gifState === "failed") return null;

  return (
    <Backdrop
      open={gifState === "showing"}
      onClick={(event) => {
        // Otherwise the snackbar treats this as a click away and closes the message too
        event.stopPropagation();
        setGifState("done");
      }}
      data-testid="gif-overlay"
      sx={{
        zIndex: (theme) => theme.zIndex.snackbar - 1,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        pointerEvents: onLoginPage ? "none" : "auto",
      }}
    >
      <Box
        key={showCount}
        component="img"
        src={GIF_OVERLAY_SRC}
        alt=""
        data-testid="gif-overlay-image"
        onLoad={() => setGifState("showing")}
        onError={() => setGifState("failed")}
        sx={{ width: "90vw", height: "90vh", objectFit: "contain" }}
      />
    </Backdrop>
  );
};
